import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { Income } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, deleteIncome, fetchCashflow, fetchIncomes, type CashflowResponse } from "../services/expensesService";
import { Chip, ErrorBox, Kpi, Notice, Page, Spinner } from "../components/ui";
import ExpectedIncomeCard from "../components/ExpectedIncomeCard";
import IncomeDialog, { type IncomeDialogMode } from "../components/IncomeDialog";
import { currentMonth, lastDayOf, money, monthLabel, shiftMonth } from "../lib/format";
import { incomeDialogFromSearch } from "../lib/incomeRoutes";
import { ui } from "../theme";

// Flujo → Ingresos (A): lo que entró, quién lo pagó y cada depósito. Los
// ingresos se cargan desde los estados de cuenta, a mano o con IA (#1274).

const RANGES = [6, 12, 24];
const INCOME_COLOR = "#2a78d6";

export default function IncomeSection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [months, setMonths] = useState(12);
  const [data, setData] = useState<CashflowResponse | null>(null);
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [error, setError] = useState<string | null>(null);
  // «Importar con IA» manda aquí con `?new=ia` cuando el documento es un ingreso.
  const [dialog, setDialog] = useState<IncomeDialogMode | null>(() => incomeDialogFromSearch(window.location.search));

  const load = useCallback(async () => {
    const to = currentMonth();
    const from = shiftMonth(to, -(months - 1));
    try {
      const [flow, list] = await Promise.all([fetchCashflow({ from, to }), fetchIncomes({ from: `${from}-01`, to: lastDayOf(to) })]);
      setData(flow);
      setIncomes(list);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err) || "load");
    }
  }, [months]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  // `?new=` ya hizo su trabajo: se quita para que recargar no reabra el modal.
  useEffect(() => {
    if (incomeDialogFromSearch(window.location.search)) window.history.replaceState(window.history.state, "", window.location.pathname);
  }, []);

  const remove = async (id: string) => {
    try {
      await deleteIncome(id);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err) || "load");
    }
  };

  const lang = i18n.language;
  const chart = (data?.months || []).map((m) => ({ label: monthLabel(m.period, lang), income: m.income }));
  const axis = isDarkMode ? "#9ca3af" : "#6b7280";
  const withIncome = (data?.months || []).filter((m) => m.income > 0);
  const average = withIncome.length ? (data?.totals.income || 0) / withIncome.length : null;
  const top = data?.payers[0];
  const topShare = top && data && data.totals.income > 0 ? Math.round((top.total / data.totals.income) * 100) : null;

  return (
    <Page
      title={t("expenses.menu.income")}
      subtitle={t("expenses.income.subtitle")}
      actions={
        <>
          <select aria-label={t("expenses.summary.range")} className={`${c.inputBase} w-auto`} value={months} onChange={(e) => setMonths(Number(e.target.value))}>
            {RANGES.map((r) => (
              <option key={r} value={r}>
                {t("expenses.summary.lastMonths", { count: r })}
              </option>
            ))}
          </select>
          <button type="button" className={c.primary} onClick={() => setDialog("manual")}>
            <i className="ri-add-line mr-1" />
            {t("expenses.cashflow.addIncome")}
          </button>
        </>
      }
    >
      <ErrorBox message={error === "load" ? t("expenses.errors.load") : error} />
      {!data && !error && <Spinner />}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label={t("expenses.cashflow.income")} value={money(data.totals.income, "MXN", lang)} hint={t("expenses.cashflow.incomeHint", { count: data.incomeCount })} />
            <Kpi label={t("expenses.income.average")} value={money(average, "MXN", lang)} hint={t("expenses.income.averageHint", { count: withIncome.length })} />
            <Kpi label={t("expenses.income.payers")} value={String(data.payers.length)} hint={t("expenses.income.payersHint")} />
            <Kpi
              label={t("expenses.income.topPayer")}
              value={topShare == null ? "—" : `${topShare}%`}
              hint={top ? top.payer : undefined}
              tone={topShare != null && topShare >= 50 ? "bad" : undefined}
            />
          </div>
          {(data.totals.refunds > 0 || data.totals.personalIncome > 0) && (
            <p className={`text-xs ${c.muted}`}>
              {t("expenses.cashflow.refundsNote", { refunds: money(data.totals.refunds, "MXN", lang), personalIncome: money(data.totals.personalIncome, "MXN", lang) })}
            </p>
          )}
          {data.withoutRate > 0 && <Notice tone="warn">{t("expenses.list.withoutRate", { count: data.withoutRate })}</Notice>}
          {data.incomeCount === 0 && <Notice tone="info">{t("expenses.cashflow.noIncome")}</Notice>}

          {/* Contratos corriendo, por cobrar y pronóstico del CRM (#1290). */}
          <ExpectedIncomeCard refreshKey={incomes} />

          <div className={c.card}>
            <h3 className={`mb-3 text-sm font-semibold ${c.text}`}>{t("expenses.income.byMonth")}</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#333" : "#e5e7eb"} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: axis }} />
                  <YAxis tick={{ fontSize: 11, fill: axis }} tickFormatter={(v) => `$${Math.round(Number(v) / 1000)}k`} width={48} />
                  <Tooltip formatter={(v: number) => [money(v, "MXN", lang), t("expenses.cashflow.income")]} contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="income" fill={INCOME_COLOR} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={c.card}>
            <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>{t("expenses.cashflow.payers")}</h3>
            {data.payers.length === 0 ? (
              <p className={`text-xs ${c.muted}`}>{t("expenses.cashflow.noIncome")}</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {data.payers.map((p) => (
                    <tr key={p.payer} className={`border-t ${c.border} ${c.text}`}>
                      <td className="py-1.5">
                        {p.payer}
                        <Chip className="ml-2 bg-sky-500/10 text-sky-600">{t(`expenses.cashflow.payerType.${p.payerType}`)}</Chip>
                        {(p.entities?.length ?? 0) > 0 && !(p.entities.length === 1 && p.entities[0] === p.payer) && (
                          <div className={`text-xs ${c.muted}`} data-testid="payer-entities">
                            {t("expenses.cashflow.entities", { count: p.entities.length })}: {p.entities.join(" · ")}
                          </div>
                        )}
                      </td>
                      <td className={`py-1.5 text-right text-xs ${c.muted}`}>{t("expenses.cashflow.deposits", { count: p.count })}</td>
                      <td className={`py-1.5 text-right text-xs tabular-nums ${c.muted}`}>{data.totals.income > 0 ? `${Math.round((p.total / data.totals.income) * 100)}%` : ""}</td>
                      <td className="py-1.5 text-right font-semibold tabular-nums">{money(p.total, "MXN", lang)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className={c.card}>
            <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>{t("expenses.cashflow.incomeList")}</h3>
            {incomes.length === 0 ? (
              <p className={`text-xs ${c.muted}`}>{t("expenses.cashflow.noIncome")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`text-left text-xs ${c.muted}`}>
                      <th className="py-1">{t("expenses.fields.date")}</th>
                      <th className="py-1">{t("expenses.cashflow.payer")}</th>
                      <th className="py-1">{t("expenses.cashflow.concept")}</th>
                      <th className="py-1 text-right">{t("expenses.fields.amount")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {incomes.map((i) => (
                      <tr key={i.id} className={`border-t ${c.border} ${c.text}`}>
                        <td className="whitespace-nowrap py-1.5 tabular-nums">{i.date}</td>
                        <td className="py-1.5">
                          {i.payer}
                          {i.client && <Chip className="ml-2 bg-sky-500/10 text-sky-600">{i.client}</Chip>}
                          {i.source === "estado_cuenta" && <Chip className="ml-2 bg-slate-500/10 text-slate-500">{t("expenses.cashflow.fromStatement")}</Chip>}
                          {i.invoiceNumber && (
                            <a href="/crm/negocios/facturas" title={t("expenses.cashflow.invoiceHint")} className="ml-2 whitespace-nowrap text-xs text-sky-600 hover:underline">
                              <i className="ri-bill-line mr-0.5" />
                              {i.invoiceNumber}
                            </a>
                          )}
                        </td>
                        <td className={`py-1.5 text-xs ${c.muted}`}>{[i.concept, i.bank, i.account].filter(Boolean).join(" · ") || "—"}</td>
                        <td className="whitespace-nowrap py-1.5 text-right font-semibold tabular-nums">{money(i.amount, i.currency, lang)}</td>
                        <td className="py-1.5 text-right">
                          <button type="button" className={`rounded p-1 text-xs ${c.muted} hover:text-red-500`} aria-label={t("expenses.cashflow.deleteIncome")} onClick={() => remove(i.id)}>
                            <i className="ri-delete-bin-line" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <IncomeDialog open={dialog != null} initialMode={dialog || "manual"} onClose={() => setDialog(null)} onSaved={load} />
    </Page>
  );
}
